import React, { useCallback, useMemo, useState } from "react";
import { StyleSheet, View, Pressable, ActivityIndicator, useWindowDimensions } from "react-native";
import { useTheme } from "../../hooks/use-theme";
import { BACK_COLOR, BORDER_WIDTH, BUTTON_SIZE, CONTROLS_HEIGHT } from "./constants";
import Icon from "../Icon";

type ControlProps = {
    uri: string;
    disabled?: boolean;
    onCrop: () => Promise<void>;
    onCancel: () => void;
};

const Controls: React.FC<ControlProps> = ({ onCrop, onCancel, disabled }) => {
    const { colors } = useTheme();
    const styles = useStyles();
    const [isCropping, setIsCropping] = useState<boolean>(false);

    const baseColor = colors.background;

    const doCrop = useCallback(() => {
        const _func = async () => {
            setIsCropping(true);
            try {
                await onCrop();
            } finally {
                setIsCropping(false);
            }
        };
        void _func();
    }, [onCrop]);

    return (
        <View style={styles.root}>
            <Pressable
                style={[styles.button, styles.cancelButton]}
                onPress={onCancel}
                disabled={disabled}
            >
                {isCropping ? (
                    <ActivityIndicator size={"small"} color={baseColor} />
                ) : (
                    <Icon name={"close"} size={24} color={colors.backdrop} />
                )}
            </Pressable>
            <View style={{ flexGrow: 1, flex: 1 }} />

            <Pressable
                style={[styles.button, styles.acceptButton]}
                onPress={doCrop}
                disabled={disabled}
            >
                {isCropping ? (
                    <ActivityIndicator size={"small"} color={baseColor} />
                ) : (
                    <Icon name={"check"} size={24} color={colors.secondary} />
                )}
            </Pressable>
        </View>
    );
};

const useStyles = () => {
    const { gridSize, colors } = useTheme();
    const { width } = useWindowDimensions();
    return useMemo(
        () =>
            StyleSheet.create({
                root: {
                    position: "absolute",
                    bottom: 0,
                    width: width,
                    height: CONTROLS_HEIGHT,
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    alignItems: "flex-end",
                    paddingHorizontal: gridSize,
                    paddingBottom: gridSize,
                    gap: gridSize / 2,
                    backgroundColor: BACK_COLOR,
                },
                button: {
                    width: BUTTON_SIZE,
                    height: BUTTON_SIZE,
                    justifyContent: "center",
                    alignItems: "center",
                },
                actionButton: {},
                acceptButton: {
                    borderRadius: BUTTON_SIZE / 2,
                    backgroundColor: colors.background,
                    borderColor: colors.background,
                    borderWidth: BORDER_WIDTH,
                },
                cancelButton: {
                    borderRadius: BUTTON_SIZE / 2,
                    borderColor: colors.backdrop,
                    borderWidth: BORDER_WIDTH,
                },
            }),
        [width, colors.backdrop, colors.background, gridSize],
    );
};

export default Controls;
